/**
 * ScrollVelocityContext
 * 
 * CRITICAL ARCHITECTURE: Framer Motion's useVelocity returns a MotionValue.
 * We sync this to a plain useRef via a MotionValue.on("change") subscription
 * inside useEffect. This avoids React state updates entirely — the WebGL
 * useFrame loops read velocityRef.current directly at 60fps with zero
 * re-render cost.
 * 
 * DO NOT convert velocityRef to useState. That single change would drop
 * performance from 60fps to ~15fps.
 */

import React, { createContext, useContext, useRef, useEffect } from 'react';
import { useScroll, useVelocity } from 'framer-motion';

const ScrollVelocityContext = createContext(null);

export const ScrollVelocityProvider = ({ children }) => {
    const velocityRef = useRef(0);

    // Framer Motion scroll tracking
    const { scrollY } = useScroll();
    const scrollVelocity = useVelocity(scrollY);

    useEffect(() => {
        // Subscribe to MotionValue changes — this fires on every scroll frame
        // but NEVER triggers a React re-render because we write to a ref
        const unsubscribe = scrollVelocity.on("change", (latest) => {
            velocityRef.current = latest;
        });

        return unsubscribe;
    }, [scrollVelocity]);

    return (
        <ScrollVelocityContext.Provider value={velocityRef}>
            {children}
        </ScrollVelocityContext.Provider>
    );
};

export const useScrollVelocity = () => {
    const ctx = useContext(ScrollVelocityContext);
    if (!ctx) {
        // Graceful fallback — return a ref with 0 velocity if context is missing
        // This prevents crashes if WebGL components render outside the provider
        return { current: 0 };
    }
    return ctx;
};

export default ScrollVelocityContext;
